import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MapPin, Briefcase, Users, Sparkles, PlayCircle } from "lucide-react";
import { useContent } from "@/lib/content";
import Reveal from "@/components/places/Reveal";
import VideoEmbed from "@/components/places/VideoEmbed";
import SEO from "@/components/places/SEO";
import ApplyModal from "@/components/places/ApplyModal";
import { useLang } from "@/lib/lang";
import { useLangKV } from "@/lib/useLangKV";
import { pickLangObj, TRANSLATABLE_FIELDS } from "@/lib/pickLang";

export default function Carreiras() {
  const { jobs: RAW_JOBS = [], careers_videos = [] } = useContent();
  const careers_config = useLangKV("careers_config") || {};
  const { lang } = useLang();
  const jobs = React.useMemo(
    () => (RAW_JOBS || []).map((j) => pickLangObj(j, lang, TRANSLATABLE_FIELDS.job)),
    [RAW_JOBS, lang]
  );
  const [expandedJob, setExpandedJob] = useState(null);
  const [applying, setApplying] = useState(null);

  // Accept both boolean true and legacy string "true"; only visibly-closed jobs are filtered out.
  const openJobs = jobs.filter((j) => j.is_open === true || j.is_open === "true" || j.is_open === undefined);
  const cultureBullets = careers_config.culture_bullets || [];

  return (
    <>
      <SEO title="Carreiras · Places" description="Venha ser Placer. Faça parte do nosso time." />
      <section className="theme-light pt-32 pb-24 md:pt-40 md:pb-32 relative overflow-hidden" data-testid="carreiras-hero">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-xs uppercase tracking-widest text-accent-orange mb-6 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> {careers_config.hero_eyebrow || "Trabalhe conosco"}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl">
            {careers_config.hero_title || "Venha ser Placer:"} <span className="text-accent-orange">{careers_config.hero_highlight || "faça parte do nosso time."}</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-neutral-700 leading-relaxed">
            {careers_config.hero_subtitle || "A gente constrói o futuro do marketing imobiliário no Brasil. E quer os melhores talentos por perto pra isso."}
          </p>
        </div>
      </section>

      {/* SOBRE NÓS */}
      <section className="theme-light border-t border-black/10 py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8">
          <Reveal className="md:col-span-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">{careers_config.about_eyebrow || "Sobre nós"}</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">{careers_config.about_title || "Um time. Um propósito."}</h2>
          </Reveal>
          <Reveal className="md:col-span-8" delay={0.15}>
            <div className="text-lg leading-relaxed text-neutral-700 space-y-4">
              {(careers_config.about_paragraphs || [
                "Somos uma agência de marketing imobiliário com quase 4 décadas de estrada. Trabalhamos com os maiores nomes do país e transformamos empreendimentos em cases inesquecíveis.",
                "Aqui, as pessoas vêm antes do resultado — e é por isso que os resultados vêm.",
              ]).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* IDEAL PARA VOCÊ */}
      <section className="theme-dark grain py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-xs uppercase tracking-widest text-accent-orange mb-4">{careers_config.culture_eyebrow || "Cultura"}</div>
            <h2 className="font-display text-3xl md:text-5xl leading-tight max-w-3xl mb-12">
              {careers_config.culture_title || "Independente da vaga, você é a pessoa ideal se…"}
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cultureBullets.map((b, i) => (
              <motion.div
                key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                className="border border-white/10 rounded-lg p-5 hover:border-accent-orange transition-colors"
                data-testid={`culture-${i}`}
              >
                <div className="w-8 h-8 rounded-full bg-accent-orange/20 text-accent-orange flex items-center justify-center font-bold text-xs mb-3">{String(i + 1).padStart(2, "0")}</div>
                <p className="text-sm text-neutral-200 leading-relaxed">{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VAGAS ABERTAS */}
      <section className="theme-light py-20 md:py-28 border-t border-black/10" data-testid="jobs-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-xs uppercase tracking-widest text-accent-orange mb-4 flex items-center gap-2"><Briefcase className="w-3 h-3" /> {careers_config.jobs_eyebrow || "Oportunidades"}</div>
            <h2 className="font-display text-3xl md:text-5xl leading-tight mb-12">{careers_config.jobs_title || "Vagas Abertas"} <span className="text-neutral-400 text-2xl md:text-3xl">({openJobs.length})</span></h2>
          </Reveal>
          {openJobs.length === 0 ? (
            <div className="border border-dashed border-black/15 rounded-xl p-12 text-center text-sm text-neutral-500">
              {careers_config.jobs_empty_prefix || "Não há vagas abertas no momento. Envie seu currículo mesmo assim pelo e-mail"}{" "}
              <a href={`mailto:${careers_config.jobs_email || "pessoas@agenciaplaces.com.br"}`} className="text-accent-orange hover:underline">{careers_config.jobs_email || "pessoas@agenciaplaces.com.br"}</a>.
            </div>
          ) : (
            <div className="space-y-3">
              {openJobs.map((j) => (
                <div key={j.id} className="border border-black/10 rounded-xl overflow-hidden hover:border-accent-orange transition-colors" data-testid={`job-${j.id}`}>
                  <button
                    onClick={() => setExpandedJob(expandedJob === j.id ? null : j.id)}
                    className="w-full p-5 md:p-6 flex flex-wrap items-center justify-between gap-4 text-left"
                  >
                    <div className="flex-1 min-w-[240px]">
                      <div className="font-display text-2xl md:text-3xl leading-tight">{j.title}</div>
                      <div className="flex items-center gap-3 text-xs text-neutral-500 mt-2">
                        {j.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location}</span>}
                        {j.department && <span>· {j.department}</span>}
                        {j.employment_type && <span>· {j.employment_type}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-widest text-neutral-500">{careers_config.job_expand_label || "Saiba mais"}</span>
                      <motion.div animate={{ rotate: expandedJob === j.id ? 180 : 0 }}><ChevronDown className="w-5 h-5" /></motion.div>
                    </div>
                  </button>
                  <AnimatePresence>
                    {expandedJob === j.id && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="p-5 md:p-6 pt-0 border-t border-black/5 bg-black/[0.02]">
                          {j.overview && (
                            <div className="mb-6">
                              <div className="text-xs uppercase tracking-widest text-accent-orange mb-2">{careers_config.job_overview_label || "Visão geral do cargo"}</div>
                              <p className="text-sm text-neutral-700 leading-relaxed">{j.overview}</p>
                            </div>
                          )}
                          <div className="grid md:grid-cols-3 gap-6">
                            <JobList title={careers_config.job_responsibilities_label || "Responsabilidades"} items={j.responsibilities || []} />
                            <JobList title={careers_config.job_requirements_label || "Requisitos"} items={j.requirements || []} />
                            <JobList title={careers_config.job_extras_label || "Informações adicionais"} items={j.extras || []} />
                          </div>
                          <button onClick={() => setApplying(j)} data-testid={`job-apply-${j.id}`} className="mt-6 inline-flex items-center gap-2 bg-black hover:bg-accent-orange text-white font-bold px-6 py-3 rounded-full text-sm transition">
                            {careers_config.job_apply_label || "APLICAR"} <Send className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* COMO É TRABALHAR AQUI */}
      {careers_videos.length > 0 && (
        <section className="theme-dark grain py-20 md:py-28 border-t border-white/10" data-testid="section-culture-videos">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <Reveal>
              <div className="text-xs uppercase tracking-widest text-accent-orange mb-4 flex items-center gap-2"><PlayCircle className="w-3 h-3" /> {careers_config.videos_eyebrow || "Bastidores"}</div>
              <h2 className="font-display text-3xl md:text-5xl leading-tight mb-12">{careers_config.videos_title || "Como é trabalhar aqui."}</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {careers_videos.map((v, i) => (
                <motion.div key={v.id || i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }} data-testid={`culture-video-${i}`}>
                  <VideoEmbed src={v.url || v.video_url} title={v.title || ""} aspect="aspect-video" />
                  {v.title && <div className="text-sm text-neutral-300 mt-2">{v.title}</div>}
                  {v.subtitle && <div className="text-[11px] text-neutral-500">{v.subtitle}</div>}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <AnimatePresence>
        {applying && <ApplyModal job={applying} onClose={() => setApplying(null)} />}
      </AnimatePresence>
    </>
  );
}

function JobList({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-accent-orange font-bold mb-3">{title}</div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-neutral-700 flex items-start gap-2">
            <span className="text-accent-orange mt-1">▸</span><span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Send is imported inside apply modal — export a placeholder icon here for the button
function Send({ className }) {
  // reuse lucide's Send via inline SVG to avoid double import
  return (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>);
}
